import { create } from 'zustand';

export type Role = 'PLATFORM_ADMIN' | 'TENANT_ADMIN' | 'MANAGER' | 'MEMBER';

export type SessionUser = {
  id: string;
  email: string;
  displayName: string;
  initials: string;
  avatarColor: string;
  role: Role;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  /** Uploaded picture (else the initials are shown). */
  avatarUrl?: string | null;
  /** Logo of the user's organisation, when it has one. */
  tenantLogoUrl?: string | null;
  /** Organisation role and the permissions it grants (resolved by the server; the server still checks). */
  roleId?: string | null;
  roleName?: string | null;
  permissions?: Permission[];
};

/** Permission codes of the backend catalogue (see /api/v1/tenant/permissions). */
export type Permission =
  | 'projects.view_all'
  | 'projects.create'
  | 'projects.edit'
  | 'projects.documents'
  | 'tasks.create'
  | 'tasks.request'
  | 'tasks.edit'
  | 'tasks.approve'
  | 'sprints.manage'
  | 'time.log'
  | 'calendar.manage'
  | 'threads.create'
  | 'reports.view'
  | 'reports.export'
  | 'payroll.manage'
  | 'payroll.void'
  | 'members.manage'
  | 'organization.manage'
  | 'roles.manage'
  | 'billing.manage';

export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated';

type SessionState = {
  status: SessionStatus;
  user: SessionUser | null;
  accessToken: string | null;
  setSession: (user: SessionUser, accessToken: string) => void;
  /** Refreshes the signed-in user's data (e.g. a new avatar) without touching the token. */
  updateUser: (user: SessionUser) => void;
  clear: () => void;
};

export const useSession = create<SessionState>((set) => ({
  status: 'unknown',
  user: null,
  accessToken: null,
  setSession: (user, accessToken) => set({ status: 'authenticated', user, accessToken }),
  updateUser: (user) => set({ user }),
  clear: () => set({ status: 'anonymous', user: null, accessToken: null })
}));

export const isPlatformAdmin = (user: SessionUser | null) => user?.role === 'PLATFORM_ADMIN';

/** Whether the user holds a permission (UI hint only: the backend enforces every permission). */
export const can = (user: SessionUser | null | undefined, permission: Permission) => !!user?.permissions?.includes(permission);

/** Hook form of {@link can}. */
export const useCan = (permission: Permission) => useSession((s) => can(s.user, permission));
